import {
    sortAlphabetically,
    sortEventsByMbn,
    sortEventsByTime,
    updateSportEventCount,
} from './event';
import { store } from '../store';
import { isESportEvent } from './helpers';
import { setSportsList } from '../store/actions/sportTypes';
import {
    EVENT_FILTER_OPTIONS,
    MODIFY_FILTER_TYPES,
    modifyFilters,
} from '../constants/event-filters';
import { EVENTS_PAGE_LIMIT } from '../constants/defaultValues';
import { setEvents } from '../store/actions/sportEvents';
import { updateBetLines } from '../store/actions/betSlip';
import { setAllMarketsEventRemoved } from '../store/actions/eventDetails';
import { setOutrights } from '../store/actions/outrights';

class SocketManager {
    static isEventTypeMatched = (payload, { isLive, isESports }) => {
        return payload.live === isLive && isESports === isESportEvent(payload.sportId);
    };

    static isSportMatched = (payload, sportId) => {
        return payload.sportId === sportId;
    };

    static isPeriodMatched = (periods, { startedAt }) => {
        let isMatched = true;

        if (periods) {
            const selectedPeriod = periods.find(period => period.selected);

            if (selectedPeriod.id !== EVENT_FILTER_OPTIONS.periods.all.id) {
                const { startTime, endTime } = selectedPeriod.getRange();

                if (startedAt * 1000 < startTime || startedAt * 1000 > endTime) {
                    isMatched = false;
                }
            }
        }

        return isMatched;
    };

    static updateSportsList = (sportsList, sportId, diff) => {
        const { dispatch } = store;

        const updatedSportsList = updateSportEventCount(sportsList, sportId, diff);

        dispatch(setSportsList(updatedSportsList));
    };

    static addEventToList = event => {
        const { dispatch } = store;
        const reduxStore = store.getState();

        const { locale } = reduxStore.settings;
        const { events: currentEvents, sortType, pageOffset, total } = reduxStore.sportEvents;

        const newTotal = total + 1;
        let events = [];

        if (sortType === EVENT_FILTER_OPTIONS.orders.byLeague.id) {
            const isSeasonFound = currentEvents.find(
                currentSeason => currentSeason.id === event.season.id,
            );

            if (isSeasonFound) {
                events = currentEvents.map(currentSeason => {
                    if (currentSeason.id === event.season.id) {
                        const seasonEvents = [...currentSeason.events, event];
                        seasonEvents.sort(sortEventsByTime);

                        return { ...currentSeason, events: seasonEvents };
                    }

                    return currentSeason;
                });
            } else {
                events = [...currentEvents, { ...event.season, events: [event] }];
            }

            events.sort((a, b) => {
                if (a.order === undefined || b.order === undefined) return 1;

                return a.order - b.order || sortAlphabetically(a.seasonName, b.seasonName);
            });

            if (newTotal > pageOffset && pageOffset % EVENTS_PAGE_LIMIT > 0) {
                if (events[events.length - 1].events.length === 1) {
                    events.pop();
                } else {
                    events[events.length - 1].events.pop();
                }
            }
        } else if (sortType === EVENT_FILTER_OPTIONS.orders.byTime.id) {
            events = [...currentEvents, event];
            events.sort(sortEventsByTime);
            events = events.slice(0, pageOffset);
        } else if (sortType === EVENT_FILTER_OPTIONS.orders.byMbn.id) {
            events = [...currentEvents, event];
            events.sort(sortEventsByMbn);
            events = events.slice(0, pageOffset);
        }

        dispatch(
            setEvents({
                events,
                hasNextPage: newTotal > pageOffset,
                total: newTotal,
            }),
        );

        return events;
    };

    static addOutrightToList = outright => {
        const { dispatch } = store;
        const reduxStore = store.getState();

        const { seasons: currentSeasons, pageOffset } = reduxStore.outrights;

        let seasons = [...currentSeasons, outright.season];
        const hasNextPage = seasons.length > pageOffset;

        seasons.sort((a, b) => {
            if (a.order === undefined || b.order === undefined) return 1;

            return a.order - b.order || sortAlphabetically(a.seasonName, b.seasonName);
        });

        seasons = seasons.slice(0, pageOffset);

        dispatch(
            setOutrights({
                seasons,
                hasNextPage,
            }),
        );
    };

    static removeFromEventList = payload => {
        const { dispatch } = store;
        const reduxStore = store.getState();

        const { events: currentEvents, sortType, pageOffset, total } = reduxStore.sportEvents;

        const { eventId, leagueId: seasonId } = payload;

        const newTotal = total - 1;
        let events = currentEvents;
        let isEventRemoved = false;

        if (sortType === EVENT_FILTER_OPTIONS.orders.byLeague.id) {
            const isSeasonFound = currentEvents.find(
                currentSeason => currentSeason.id === seasonId,
            );

            if (isSeasonFound) {
                events = currentEvents
                    .map(currentSeason => {
                        if (currentSeason.id === seasonId) {
                            const filteredEvents = currentSeason.events.filter(
                                event => event.id !== eventId,
                            );

                            if (filteredEvents.length !== currentSeason.events.length) {
                                isEventRemoved = true;
                            }

                            return {
                                ...currentSeason,
                                events: filteredEvents,
                            };
                        }

                        return currentSeason;
                    })
                    .filter(s => s.events.length);
            }
        } else if (
            sortType === EVENT_FILTER_OPTIONS.orders.byTime.id ||
            sortType === EVENT_FILTER_OPTIONS.orders.byMbn.id
        ) {
            events = currentEvents.filter(event => event.id !== eventId);

            if (events.length !== currentEvents.length) {
                isEventRemoved = true;
            }
        }

        if (!isEventRemoved) return;

        dispatch(
            setEvents({
                events,
                hasNextPage: newTotal > pageOffset,
                pageOffset: pageOffset - 1,
                total: newTotal,
            }),
        );

        return events;
    };

    static removeFromOutrightList = payload => {
        const { dispatch } = store;
        const reduxStore = store.getState();
        const { seasons: currentSeasons, pageOffset } = reduxStore.outrights;

        let seasons = currentSeasons.filter(season => season.currentSeasonId !== payload.eventId);

        if (seasons.length === currentSeasons.length) return;

        dispatch(
            setOutrights({
                seasons,
                pageOffset: pageOffset - 1,
            }),
        );
    };

    static updateBetLinesStatus = ({ betLines, eventId, changedProps }) => {
        if (betLines.length < 1) return;

        const { dispatch } = store;

        const newBetLines = betLines.map(betLine => {
            if (betLine.eventId === eventId) {
                return {
                    ...betLine,
                    ...changedProps,
                };
            }

            return betLine;
        });

        dispatch(updateBetLines(newBetLines));
    };

    static closeAllMarkets = (payload, allMarketsEvent) => {
        const { dispatch } = store;

        if (payload.eventId === allMarketsEvent?.id) {
            dispatch(setAllMarketsEventRemoved(true));
        }
    };

    static handleEventAdd = payload => {
        const reduxStore = store.getState();

        const { locale } = reduxStore.settings;
        const { sportsList } = reduxStore.sportTypes;
        const { betLines } = reduxStore.betSlip;
        const { filters, isESports, isLive, sportRefId } = reduxStore.sportEvents;

        SocketManager.updateBetLinesStatus({
            betLines,
            eventId: payload.event.id,
            changedProps: { closed: false, removed: false, isLive: payload.live },
        });

        if (!SocketManager.isEventTypeMatched(payload, { isLive, isESports })) return;

        SocketManager.updateSportsList(sportsList, payload.sportId, 1);

        if (!SocketManager.isSportMatched(payload, sportRefId)) return;

        if (!SocketManager.isPeriodMatched(filters.periods, { startedAt: payload.event.startedAt }))
            return;

        const { shouldModifyEvents } = modifyFilters({
            filters,
            payload: { category: payload.event.category, league: payload.event.season },
            locale,
            actionType: MODIFY_FILTER_TYPES.add,
        });

        if (!shouldModifyEvents) return;

        SocketManager.addEventToList(payload.event);
    };

    static handleOutrightAdd = payload => {
        const reduxStore = store.getState();

        const { locale } = reduxStore.settings;
        const { sportsList } = reduxStore.sportTypes;
        const { betLines } = reduxStore.betSlip;
        const { filters, sportRefId } = reduxStore.sportEvents;

        const marketCount = payload.season.markets.length;

        SocketManager.updateBetLinesStatus({
            betLines,
            eventId: payload.eventId,
            changedProps: { closed: false, removed: false, isLive: payload.live },
        });

        SocketManager.updateSportsList(sportsList, payload.sportId, marketCount);

        if (!SocketManager.isSportMatched(payload, sportRefId)) return;

        const { shouldModifyEvents } = modifyFilters({
            filters,
            payload: {
                category: payload.season.category,
                league: {
                    countryCode: payload.season.countryCode,
                    currentSeasonId: payload.season.currentSeasonId,
                    id: payload.season.id,
                    order: payload.season.order,
                    categoryId: payload.season.categoryId,
                    names: payload.season.names,
                    // categoryName: payload.season.seasonName,
                    seasonName: payload.season.seasonName,
                },
            },
            locale,
            actionType: MODIFY_FILTER_TYPES.add,
            count: marketCount,
        });

        if (!shouldModifyEvents) return;

        SocketManager.addOutrightToList(payload);
    };

    static handleEventRemove = payload => {
        const reduxStore = store.getState();

        const { locale } = reduxStore.settings;
        const { sportsList } = reduxStore.sportTypes;
        const { betLines } = reduxStore.betSlip;
        const { event: allMarketsEvent } = reduxStore.eventDetails;
        const { filters, isESports, isLive, sportRefId } = reduxStore.sportEvents;

        SocketManager.updateBetLinesStatus({
            betLines,
            eventId: payload.eventId,
            changedProps: { closed: true, removed: true, isLive: payload.live },
        });

        SocketManager.closeAllMarkets(payload, allMarketsEvent);

        if (!SocketManager.isEventTypeMatched(payload, { isLive, isESports })) return;

        SocketManager.updateSportsList(sportsList, payload.sportId, -1);

        if (!SocketManager.isSportMatched(payload, sportRefId)) return;

        if (!SocketManager.isPeriodMatched(filters.periods, { startedAt: payload.scheduleDate }))
            return;

        const { shouldModifyEvents } = modifyFilters({
            filters,
            payload: {
                category: { id: payload.categoryId },
                league: { id: payload.leagueId },
            },
            locale,
            actionType: MODIFY_FILTER_TYPES.delete,
        });

        if (!shouldModifyEvents) return;

        SocketManager.removeFromEventList(payload);
    };

    static handleOutrightRemove = payload => {
        const reduxStore = store.getState();

        const { locale } = reduxStore.settings;
        const { betLines } = reduxStore.betSlip;
        const { filters, sportRefId } = reduxStore.sportEvents;

        SocketManager.updateBetLinesStatus({
            betLines,
            eventId: payload.eventId,
            changedProps: { closed: true, removed: true, isLive: payload.live },
        });

        if (!SocketManager.isSportMatched(payload, sportRefId)) return;

        const { shouldModifyEvents } = modifyFilters({
            filters,
            payload: {
                category: { id: payload.categoryId },
                league: { id: payload.leagueId },
            },
            locale,
            actionType: MODIFY_FILTER_TYPES.delete,
        });

        if (!shouldModifyEvents) return;

        SocketManager.removeFromOutrightList(payload);
    };

    static isOutrightEvent = payload => payload.outright;
}

export default SocketManager;
